import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, CheckIcon } from "@aureskonnect/react-ui";
import { uuid } from "uuidv4";
import {
    DynamicTable,
    customColumnProps,
} from "@maherunlocker/custom-react-table";
import { useSnapshot } from "valtio";
import classnames from "classnames";

import { store } from "../../../../components/VerticalLayout/store";
import {
    store as Fidelity,
    setIsActionsClicked,
    setActions,
    setValidationAction,
    setIsEdited,
    setIsSupportCodeValid,
} from "../store";

import ErrorToast from "@components/Common/ErrorTost";
import { CustomIcons } from "./CustomIcons";
import { ConfirmationInitializationMessage } from "@components/Common/ConfirmationInitializationMessage";
import { ActionColumn } from "./ActionColumn";
import { ConfirmationActiveFidelity } from "../ConfirmationActiveFidelity";
import FidelityForm from "./FidelityForm";
import HistoryModal from "../HistoryModal";
import { useCallbackPrompt } from "@hooks/useCallbackPrompt";
import { ConfirmationUpdatingDataMessageModal } from "@components/Common/ConfirmationUpdatingDataMessageModal";
import { CustomMainColorIconButton } from "@components/Common/CustomMainColorIconButton";
import { CustomMainColorButtonWithIcon } from "@components/Common/CustomMainColorButtonWithIcon";
import { SupportCodeModal } from "@pages/Applications/DeviceAssociateModal/MoneticsApplicationForm/SupportCodeModal";
import { NumberActiveAndArchive, Selected } from "./helpes";
import NProgressWrapper from "@components/Common/NProgressWrapper";

import "./index.css";
import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";

export default function FidelityFranchise({
    isFidelityArchived,
    setIsFidelityArchived,
    setLimit,
}: any) {
    const { t } = useTranslation();
    const didMountRef = React.useRef(false);
    const { oneShop, userID, franchiseID, shopID } = useSnapshot(store);
    const {
        fidelityData,
        isActionsClicked,
        isEdited,
        actions,
        validationAction,
        modify,
        isLoading,
    } = useSnapshot(Fidelity);

    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        isEdited
    );
    const [isConsult, setIsConsult] = React.useState<boolean>(false);
    const [
        isAddNewFidelityButtonClicked,
        setIsAddNewFidelityButtonClicked,
    ] = useState(false);

    const [dataIsUpdated, setDataIsUpdated] = React.useState<boolean | number>(
        false
    );

    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [
        isInInitializationModalOpened,
        setIsInInitializationModalOpened,
    ] = React.useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [isHistoryModalOpened, setIsHistoryModalOpened] = React.useState<
        boolean
    >(false);
    const [fidelity, setFidelity] = useState<any>([]);
    const [shopId, setShopId] = React.useState<string | number>(
        oneShop ? shopID : "0"
    );
    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [dataTable, setDataTable] = React.useState<any>({});
    const [isAddFidelityClicked, setIsAddFidelityClicked] = React.useState<
        boolean
    >(false);
    const [fidelityChoiceIndex, setFidelityChoiceIndex] = React.useState<
        number
    >(0);
    const [isModalOpenedSupport, setIsModalOpenedSupport] = React.useState<
        boolean
    >(false);
    const [codeSupport, setCodeSupport] = React.useState<string>("");
    const [isValidCode, setIsValidCode] = React.useState(false);
    const [key, setKey] = useState<any>("");
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const urlArchive: string = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity?userId=${userID}&shopId=${shopId}&franchiseId=${franchiseID}`;
    const urlActive: string = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity?userId=${userID}&shopId=${shopId}&franchiseId=${franchiseID}&type=activated`;

    let arrayOfCustomColumns: customColumnProps[] = [
        {
            indexOFColumn: 4,
            columnName: t("Centralized loyalty"),
            customJsx: (e: any) => (
                <span>
                    {e.selectedRow[t("State")] === true && (
                        <CheckIcon
                            className="mt-1 ml-1 cmn_chk_selectButton"
                            height={30}
                            width={30}
                        />
                    )}
                </span>
            ),
            filterName: t("Centralized loyalty"),
            canFilter: true,
        },
        {
            indexOFColumn: 7,
            columnName: t("Actions"),
            customJsx: (e: any) => (
                <CustomIcons
                    e={e}
                    setShopId={setShopId}
                    setFidelity={setFidelity}
                    setIsModalOpened={setIsModalOpened}
                    isModalOpened={isModalOpened}
                    setDataIsUpdated={setDataIsUpdated}
                    setSelectedRows={setSelectedRows}
                    isFidelityArchived={isFidelityArchived}
                    isAddFidelityClicked={isAddFidelityClicked}
                    setIsAddFidelityClicked={setIsAddFidelityClicked}
                    selectedRows={selectedRows}
                    setLocalFilterActive={setLocalFilterActive}
                    setIsModalOpenedSupport={setIsModalOpenedSupport}
                    setIsConsult={setIsConsult}
                    setCodeSupport={setCodeSupport}
                />
            ),
            disableFilter: false,
        },
    ];

    React.useEffect(() => {
        if (filterActive) setIsAddFidelityClicked(false);
    }, [filterActive]);

    React.useEffect(() => {
        Selected(selectedRows, dataTable, oneShop);
    }, [selectedRows, dataTable, t, oneShop]);

    React.useEffect(() => {
        NumberActiveAndArchive(dataTable);
        // eslint-disable-next-line
    }, [dataTable]);

    React.useEffect(() => {
        setActions(confirmNavigation);
        setIsActionsClicked(showPrompt as boolean);
        setValidationAction(cancelNavigation);
    }, [showPrompt, confirmNavigation, cancelNavigation]);

    React.useEffect(() => {
        setIsAddFidelityClicked(false);
        setKey(uuid());
    }, [t]);

    React.useEffect(() => {
        setIsSupportCodeValid(
            !isModalOpenedSupport && isValidCode === false && codeSupport !== ""
        );
    }, [isModalOpenedSupport, isValidCode, codeSupport]);

    React.useEffect(() => {
        setIsAddFidelityClicked(false);
        if (didMountRef.current) {
            setDataIsUpdated(true);
        } else {
            didMountRef.current = true;
        }
    }, [i18nextLng]);

    return (
        <React.Fragment key={key}>
            <div
                style={{
                    display: "grid",
                    minHeight: oneShop ? "650px" : "600px",
                    gridTemplateColumns: isAddFidelityClicked
                        ? "2fr 1fr "
                        : "auto",
                    gridColumnGap: "4px",
                    border: 0,
                }}
            >
                <DynamicTable
                    key={`${dataIsUpdated}`}
                    url={isFidelityArchived ? urlArchive : urlActive}
                    requestHeader={{ "Accept-Language": i18nextLng }}
                    canExpand={false}
                    canSelect
                    customSelect
                    setData={setDataTable}
                    setSelectedRows={setSelectedRows}
                    customJsxSideFilterButton={
                        <React.Fragment>
                            {modify === true ? (
                                <>
                                    {isAddFidelityClicked !== true ? (
                                        <div className="d-flex justify-content-start align-items-center ml-2">
                                            <span
                                                onClick={() => {
                                                    setFidelity([]);
                                                    if (
                                                        fidelityData.data
                                                            .length > 0 &&
                                                        (oneShop ||
                                                            (fidelityData.data.some(
                                                                (el: any) =>
                                                                    el.fidelityChoiceIndex !==
                                                                    1
                                                            ) &&
                                                                !oneShop))
                                                    ) {
                                                        ErrorToast(
                                                            t(
                                                                oneShop
                                                                    ? "Attention, a shop cannot have more than one setting fidelity"
                                                                    : "Attention, a franchise cannot have more than one setting fidelity"
                                                            )
                                                        );
                                                    } else {
                                                        setFidelityChoiceIndex(
                                                            0
                                                        );
                                                        setIsAddFidelityClicked(
                                                            !isAddFidelityClicked
                                                        );
                                                    }

                                                    setLocalFilterActive(false);
                                                }}
                                                className={`${classnames(
                                                    "w-100 ml-3 cmn_btn_openForm",
                                                    {
                                                        "not-allowed-icon__clz":
                                                            isFidelityArchived ||
                                                            isLoading,
                                                    }
                                                )}`}
                                            >
                                                <CustomMainColorButtonWithIcon
                                                    icon="PlusIcon"
                                                    iconPosition="left"
                                                    rounded
                                                    variant="primary"
                                                >
                                                    {t("Add fidelity")}
                                                </CustomMainColorButtonWithIcon>
                                                <div></div>
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="d-flex justify-content-start">
                                            <CustomMainColorIconButton
                                                icon="PlusIcon"
                                                className=" fed_btn_FRreset"
                                                rounded
                                                disabled={
                                                    fidelity.length === 0
                                                        ? false
                                                        : true
                                                }
                                                onClick={() => {
                                                    if (isEdited === true)
                                                        setIsInInitializationModalOpened(
                                                            true
                                                        );
                                                }}
                                            >
                                                <PlusIcon
                                                    height={20}
                                                    width={20}
                                                    fill="white"
                                                />
                                            </CustomMainColorIconButton>
                                            <div className="mt-3"></div>
                                        </div>
                                    )}
                                </>
                            ) : null}
                            <ActionColumn
                                selectedRows={selectedRows}
                                setDataIsUpdated={setDataIsUpdated}
                                setIsModalOpened={setIsModalOpened}
                                isModalOpened={isModalOpened}
                                isFidelityArchived={isFidelityArchived}
                                setIsFidelityArchived={setIsFidelityArchived}
                                setIsAddFidelityClicked={
                                    setIsAddFidelityClicked
                                }
                                setIsModalOpenedSupport={
                                    setIsModalOpenedSupport
                                }
                                setLimit={setLimit}
                            />
                        </React.Fragment>
                    }
                    canResize
                    showGlobalFilter
                    showFilter
                    canMovedCheckboxLeftOnExpand
                    setLocalFilterActive={setLocalFilterActive}
                    filterActive={filterActive}
                    actionColumn={() => <></>}
                    arrayOfCustomColumns={arrayOfCustomColumns}
                    setDataIsUpdated={setDataIsUpdated}
                    dataIsUpdated={dataIsUpdated}
                    elevationTable={1}
                    minHeight={oneShop ? "500px" : "450px"}
                    maxHeight={oneShop ? "500px" : "450px"}
                    name="fidelityForm"
                />
                {isAddFidelityClicked ? (
                    <FidelityForm
                        isAddFidelityClicked={isAddFidelityClicked}
                        setIsAddFidelityClicked={setIsAddFidelityClicked!}
                        setDataIsUpdated={setDataIsUpdated}
                        dataFidelity={fidelity}
                        isAddNewFidelityButtonClicked={
                            isAddNewFidelityButtonClicked
                        }
                        setDataFidelity={setFidelity}
                        setLocalFilterActive={setLocalFilterActive}
                        fidelityChoiceIndex={fidelityChoiceIndex}
                        setFidelityChoiceIndex={setFidelityChoiceIndex}
                        setIsConsult={setIsConsult}
                        isConsult={isConsult}
                    />
                ) : null}
            </div>
            {isActionsClicked === true ? (
                <ConfirmationUpdatingDataMessageModal
                    isModalOpened={isActionsClicked}
                    setIsModalOpened={setIsActionsClicked}
                    validationAction={validationAction}
                    actions={actions}
                    setIsEdited={setIsEdited}
                />
            ) : null}

            <ConfirmationActiveFidelity
                setIsModalOpened={setIsModalOpened}
                isModalOpened={isModalOpened}
            />

            {isInInitializationModalOpened === true ? (
                <ConfirmationInitializationMessage
                    isModalOpened={isInInitializationModalOpened}
                    setIsModalOpened={setIsInInitializationModalOpened}
                    setIsClicked={setIsAddNewFidelityButtonClicked}
                    isClicked={isAddNewFidelityButtonClicked}
                    setIsEdited={setIsEdited}
                />
            ) : null}
            {isHistoryModalOpened ? (
                <HistoryModal
                    ids={selectedRows}
                    setIsModalOpened={setIsHistoryModalOpened}
                    isModalOpened={isHistoryModalOpened}
                    setDataUpdated={setDataIsUpdated}
                />
            ) : null}
            {isModalOpenedSupport ? (
                <SupportCodeModal
                    isModalOpened={isModalOpenedSupport}
                    setIsModalOpened={setIsModalOpenedSupport}
                    codeSupport={codeSupport}
                    setCodeSupport={setCodeSupport}
                    isValidCode={isValidCode}
                    setIsValidCode={setIsValidCode}
                />
            ) : null}
            <NProgressWrapper isLoading={isLoading} />
        </React.Fragment>
    );
}
